import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, Clock, Wrench } from "lucide-react";
import type { OverallSystemStatus, ProcessedGroup } from "../types";

interface StatusHeroProps {
  status: OverallSystemStatus;
  uptimePercent: number;
  groups: ProcessedGroup[];
}

export const StatusHero: React.FC<StatusHeroProps> = ({
  status,
  uptimePercent,
  groups,
}) => {
  const allMonitors = groups.flatMap((g) => g.monitors);
  const operationalCount = allMonitors.filter((m) =>
    (m.status === "operational" || m.status === "degraded") &&
    m.heartbeats.at(-1)?.status === 1
  ).length;
  const totalCount = allMonitors.length;
  const allLatestChecksSuccessful = totalCount > 0 && operationalCount === totalCount;

  const getStatusConfig = () => {
    switch (status) {
      case "operational":
        if (!allLatestChecksSuccessful) {
          return {
            title: "A legutóbbi ellenőrzések hiányosak",
            description:
              "Egyes szolgáltatásokhoz nincs sikeres legutóbbi ellenőrzés. A 24 órás rendelkezésre állás az előzményeket mutatja.",
            icon: Clock,
            borderAccent: "border-t-[3px] border-t-[#dedbd4]",
            badgeClass: "bg-[#f3f1ec] text-[#62666b] border-[#dedbd4]",
            iconColor: "text-[#62666b]",
          };
        }
        return {
          title: "Minden rendszer zavartalanul üzemel",
          description:
            "Valamennyi jogi dokumentum-automatizációs és háttérszolgáltatás az elvárt szinten működik.",
          icon: CheckCircle2,
          borderAccent: "border-t-[3px] border-t-[#246047]",
          badgeClass: "bg-[#e7f1eb] text-[#246047] border-[#b8dec9]",
          iconColor: "text-[#246047]",
        };
      case "degraded":
        return {
          title: allLatestChecksSuccessful
            ? "A legutóbbi ellenőrzések sikeresek"
            : "Részleges szolgáltatásprobléma",
          description: allLatestChecksSuccessful
            ? "A 24 órás rendelkezésre állás az ellenőrzések előzményeit mutatja. Az ismert problémák részletei az incidensközleményekben láthatók."
            : "Egyes szolgáltatások legutóbbi ellenőrzése nem sikeres vagy hiányzik. A részleteket az alábbi rendszerlista mutatja.",
          icon: AlertTriangle,
          borderAccent: "border-t-[3px] border-t-[#c5a880]",
          badgeClass: "bg-[#faf0d9] text-[#775219] border-[#edd6a4]",
          iconColor: "text-[#775219]",
        };
      case "outage":
        return {
          title: "Kritikus rendszerkimaradás",
          description:
            "A rendszerállapot kimaradást jelez. A legutóbbi ellenőrzések és a közzétett incidensközlemények részletei alább láthatók.",
          icon: XCircle,
          borderAccent: "border-t-[3px] border-t-[#922d35]",
          badgeClass: "bg-[#fae9ea] text-[#922d35] border-[#f0bec2]",
          iconColor: "text-[#922d35]",
        };
      case "maintenance":
        return {
          title: "Ütemezett rendszerkarbantartás",
          description:
            "Tervezett fejlesztési és karbantartási munkálatok zajlanak a megbízhatóság érdekében.",
          icon: Wrench,
          borderAccent: "border-t-[3px] border-t-[#365570]",
          badgeClass: "bg-[#eaf0f5] text-[#365570] border-[#c6d8e6]",
          iconColor: "text-[#365570]",
        };
      case "unknown":
        return {
          title: "A telemetriai állapot jelenleg nem érhető el",
          description:
            "Nem sikerült közvetlen kapcsolatot létesíteni a monitoring kiszolgálóval. Kérjük, próbálja újra később.",
          icon: AlertTriangle,
          borderAccent: "border-t-[3px] border-t-[#c5a880]",
          badgeClass: "bg-[#faf0d9] text-[#775219] border-[#edd6a4]",
          iconColor: "text-[#775219]",
        };
      case "loading":
      default:
        return {
          title: "Rendszerállapot ellenőrzése...",
          description: "Kapcsolódás a rendszerekhez...",
          icon: Clock,
          borderAccent: "border-t-[3px] border-t-[#dedbd4]",
          badgeClass: "bg-[#f3f1ec] text-[#62666b] border-[#dedbd4]",
          iconColor: "text-[#62666b]",
        };
    }
  };

  const current = getStatusConfig();
  const Icon = current.icon;
  // Until the first fetch resolves there are no numbers to show; avoid fake "100% / 0 of 0".
  const hasMetrics = status !== "loading";

  return (
    <div
      className={`bg-white border border-[#dedbd4] rounded-[0.375rem] p-6 sm:p-7 ${current.borderAccent}`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        {/* Left: Main status lockup */}
        <div className="flex items-start gap-4">
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-[0.375rem] border flex items-center justify-center ${current.badgeClass}`}
          >
            <Icon className={`w-6 h-6 ${current.iconColor}`} />
          </div>

          <div>
            <h2 className="text-2xl sm:text-[1.75rem] font-serif font-semibold text-[#1b2a47] tracking-tight mb-1">
              {current.title}
            </h2>
            <p className="text-sm text-[#62666b] max-w-2xl leading-relaxed">
              {current.description}
            </p>
          </div>
        </div>

        {/* Right: Key metrics */}
        <div className="flex items-center gap-5 border-t md:border-t-0 md:border-l border-[#dedbd4] pt-4 md:pt-0 md:pl-7">
          <div>
            <div className="text-[11px] font-semibold text-[#62666b] uppercase tracking-wider mb-0.5">
              24h rendelkezésre állás
            </div>
            <div className="text-2xl font-mono font-semibold text-[#1b2a47]">
              {hasMetrics ? `${uptimePercent.toFixed(2)}%` : "–"}
            </div>
          </div>

          <div className="w-px h-8 bg-[#dedbd4]" />

          <div>
            <div className="text-[11px] font-semibold text-[#62666b] uppercase tracking-wider mb-0.5">
              Elérhető rendszerek
            </div>
            <div className="text-2xl font-mono font-semibold text-[#1b2a47]">
              {hasMetrics ? (
                <>
                  {operationalCount}
                  <span className="text-sm font-normal text-[#8c8982] ml-1">
                    / {totalCount}
                  </span>
                </>
              ) : (
                "–"
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
